import * as Api from '@ViewModels';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';

// Constants for maintainability
const CRITICAL_PAST_DUE_THRESHOLD_DAYS = 20;
const TOAST_DURATION_MS = 60000; // 60 seconds

/**
 * Hook for managing past due billing warning messages
 * Shows different toast notifications based on user role and days overdue
 * @param userSession - The current user session context
 * @returns Object containing billing state information and actions
 */
export const usePastDueBillingWarning = (
	userSession: Api.UserSessionContext
): {
	shouldShowMessage: boolean;
	isPastDue: boolean;
	isAdmin: boolean;
	daysPastDue: number;
	isMoreThan20DaysPastDue: boolean;
	selfServicePaymentUrl: string | null;
} => {
	const [hasShownToast, setHasShownToast] = React.useState(false);
	const [selfServicePaymentUrl, setSelfServicePaymentUrl] = React.useState<string | null>(null);
	const [loadingPaymentUrl, setLoadingPaymentUrl] = React.useState(false);
	const [hasAttemptedPaymentUrl, setHasAttemptedPaymentUrl] = React.useState(false);

	const account = userSession?.account;
	const isPastDue = account?.planDetails?.billingState?.toLowerCase() === 'past_due';
	const isAdmin = userSession?.isAdmin || false;
	const renewalDate = account?.planDetails?.renewalDate;

	// Calculate days past due
	const daysPastDue = renewalDate ? moment().diff(moment(renewalDate), 'days') : 0;
	const isMoreThan20DaysPastDue = daysPastDue > CRITICAL_PAST_DUE_THRESHOLD_DAYS;

	// Determine if message should be shown
	const shouldShowMessage = isPastDue && (isAdmin || isMoreThan20DaysPastDue);

	// Load payment URL for admins
	React.useEffect(() => {
		if (isPastDue && isAdmin && !hasAttemptedPaymentUrl && !loadingPaymentUrl) {
			setLoadingPaymentUrl(true);
			setHasAttemptedPaymentUrl(true);
			const accountId = account?.id;
			if (accountId) {
				account
					.getSelfServicePaymentUrl(accountId)
					.then(result => {
						if (result.success) {
							setSelfServicePaymentUrl(result.value);
						}
					})
					.catch(error => {
						console.error('Could not load the payment link', error);
					})
					.finally(() => {
						setLoadingPaymentUrl(false);
					});
			} else {
				setLoadingPaymentUrl(false);
			}
		}
	}, [isPastDue, isAdmin, account, hasAttemptedPaymentUrl, loadingPaymentUrl]);

	// Show warning messages
	React.useEffect(() => {
		if (shouldShowMessage && !hasShownToast && (!isAdmin || (hasAttemptedPaymentUrl && !loadingPaymentUrl))) {
			const toastId = 'past-due-billing-warning';

			if (isAdmin) {
				// For admins: Show appropriate toast based on severity
				if (isMoreThan20DaysPastDue) {
					// Admin + 20+ days: Error toast with billing button
					toast.error(
						React.createElement('div', {}, React.createElement('strong', {}, 'URGENT: Account Payment Overdue')),
						{
							id: toastId,
							description: `This account's billing is past due (${daysPastDue} days overdue). The account will be canceled and deleted soon if billing information is not updated.`,
							duration: TOAST_DURATION_MS,
							style: {
								minWidth: '600px',
							},
							action: selfServicePaymentUrl
								? {
										label: 'Update Billing',
										onClick: () => {
											window.open(selfServicePaymentUrl, '_blank', 'noopener,noreferrer');
										},
									}
								: undefined,
						}
					);
				} else {
					// Admin + less than 20 days: Warning toast with billing button
					toast.warning(
						React.createElement('div', {}, React.createElement('strong', {}, 'Your account billing is past due')),
						{
							id: toastId,
							description: 'Please update your billing information to avoid service interruption.',
							duration: TOAST_DURATION_MS,
							style: {
								minWidth: '600px',
							},
							action: selfServicePaymentUrl
								? {
										label: 'Update Billing',
										onClick: () => {
											window.open(selfServicePaymentUrl, '_blank', 'noopener,noreferrer');
										},
									}
								: undefined,
						}
					);
				}
			} else {
				// For non-admin users after 20+ days: Show error toast
				toast.error(React.createElement('div', {}, React.createElement('strong', {}, 'Account Payment Overdue')), {
					id: toastId,
					description: `This account's billing is past due (${daysPastDue} days overdue). The account will be canceled and deleted soon if billing information is not updated. Please contact your account administrator.`,
					duration: TOAST_DURATION_MS,
					style: {
						minWidth: '600px',
					},
				});
			}
			setHasShownToast(true);
		}
	}, [
		shouldShowMessage,
		hasShownToast,
		isAdmin,
		selfServicePaymentUrl,
		daysPastDue,
		isMoreThan20DaysPastDue,
		hasAttemptedPaymentUrl,
		loadingPaymentUrl,
	]);

	return {
		shouldShowMessage,
		isPastDue,
		isAdmin,
		daysPastDue,
		isMoreThan20DaysPastDue,
		selfServicePaymentUrl,
	};
};
